import React, { useEffect, useState } from 'react'
import {
  Button, Form, Input, InputNumber, Modal, Select, Space, Spin, Switch, Tabs, Tag, Upload, Popconfirm, message,
} from 'antd'
import {
  MinusCircleOutlined, PlusOutlined, UploadOutlined, DeleteOutlined, ExclamationCircleOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '~/Store'
import {
  getIsConfirmLoading,
  getModalVisibility,
  getProductSelected,
  ModalName,
  ProductActions,
} from '~/Store/Products'
import {
  getApplications,
  getIsConfirmLoading as getIsApplicationConfirmLoading,
  getIsLoading as getIsLoadingApplications,
  getIsLogoSaving,
  OpenIddictApplicationActions,
} from '~/Store/OpenIddictApplications'
import { resolveIdentityAssetUrl } from '~/Services'
import { AVAILABLE_SCOPES } from './application.constants'
import { LoginBrandingSection } from './LoginBrandingSection'

const { Option } = Select

interface OriginField {
  origin: string
}

interface UriField {
  uri: string
}

// Chỉ raster format an toàn (khớp validate backend LocalClientLogoStorage) — SVG cố tình không cho phép.
const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp']
const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024
// ID-07 — khớp OpenIddictServiceExtensions.Min/MaxAccessTokenLifetimeSeconds phía identity-service.
const MIN_ACCESS_TOKEN_LIFETIME_SECONDS = 300
const MAX_ACCESS_TOKEN_LIFETIME_SECONDS = 28800

// Product và OpenIddict Application được gộp thành 1 màn hình: tạo Application ở identity-service tự
// động tạo kèm Product cùng ClientId (OpenIddictApplicationService.CreateAsync -> ProductService.
// EnsureProductExistsAsync) nên không còn "tạo Product độc lập" — form Create ở đây luôn tạo Application.
// Ở Edit, Name (Product) và các field còn lại (Application) được lưu bằng 2 request riêng
// (updateProduct + updateApplication), submit cùng lúc.
export const ProductFormModal = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const visible = useAppSelector(getModalVisibility(ModalName.Product))
  const isSavingProduct = useAppSelector(getIsConfirmLoading)
  const isSavingApplication = useAppSelector(getIsApplicationConfirmLoading)
  const isLoadingApplications = useAppSelector(getIsLoadingApplications)
  const isLogoSaving = useAppSelector(getIsLogoSaving)
  const product = useAppSelector(getProductSelected)
  const applications = useAppSelector(getApplications)
  const [form] = Form.useForm()

  // Không bind qua Form.Item (name/valuePropName) như các field khác — Switch phải giữ nguyên vị trí
  // cũ cho tới khi Modal.confirm được xác nhận, nên tự quản lý state thay vì để Form cập nhật checked
  // ngay khi click (xem onSubmit: giá trị commit mới được gộp vào payload lúc lưu).
  const [allowPasswordGrant, setAllowPasswordGrant] = useState(false)

  const isEdit = !!product
  // Product không lưu applicationId — khớp qua ClientId (1-1, ProductsController chặn trùng ClientId).
  const matchedApplication = isEdit
    ? applications.find(a => a.clientId === product.clientId)
    : undefined

  useEffect(() => {
    dispatch(OpenIddictApplicationActions.getApplications())
  }, [])

  useEffect(() => {
    if (product) {
      form.setFieldsValue({
        name: product.name,
        clientId: product.clientId,
        displayName: matchedApplication?.displayName,
        redirectUris: (matchedApplication?.redirectUris || []).map(uri => ({
          uri,
        })),
        postLogoutRedirectUris: (
          matchedApplication?.postLogoutRedirectUris || []
        ).map(uri => ({ uri })),
        scopes: matchedApplication?.scopes || [],
        allowedOrigins: (matchedApplication?.allowedOrigins || []).map(origin => ({
          origin,
        })),
        accessTokenLifetimeSeconds: matchedApplication?.accessTokenLifetimeSeconds ?? undefined,
      })
      setAllowPasswordGrant(!!matchedApplication?.allowPasswordGrant)
    } else {
      form.resetFields()
      setAllowPasswordGrant(false)
    }
  }, [product, matchedApplication])

  // Modal.confirm (giữa màn hình) thay vì Popconfirm (popover neo theo vị trí Switch) — bên trong
  // modal có scroll riêng (bodyStyle overflowY), Popconfirm định vị sai khi trigger nằm trong vùng
  // scroll đó.
  const onTogglePasswordGrant = (checked: boolean) => {
    Modal.confirm({
      title: checked
        ? t('admin.confirmEnablePasswordGrant')
        : t('admin.confirmDisablePasswordGrant'),
      icon: <ExclamationCircleOutlined />,
      okText: t('admin.yes'),
      cancelText: t('cancel'),
      okButtonProps: { 'data-testid': 'product-allow-password-grant-confirm-ok' },
      cancelButtonProps: { 'data-testid': 'product-allow-password-grant-confirm-cancel' },
      onOk: () => setAllowPasswordGrant(checked),
    })
  }

  const close = () => {
    dispatch(ProductActions.setProductSelected(undefined))
    dispatch(
      ProductActions.setModalVisibility({
        name: ModalName.Product,
        visible: false,
      }),
    )
  }

  // Validate client-side trước khi gọi API — khớp giới hạn backend (LocalClientLogoStorage) để feedback
  // tức thời thay vì round-trip lên server rồi mới báo lỗi.
  const beforeUploadLogo = (file: File) => {
    if (!matchedApplication) {
      return Upload.LIST_IGNORE
    }
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      message.error(t('admin.invalidLogoType'))
      return Upload.LIST_IGNORE
    }
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      message.error(t('admin.logoTooLarge'))
      return Upload.LIST_IGNORE
    }

    dispatch(OpenIddictApplicationActions.uploadLogo({ id: matchedApplication.id, file }))
    // false = không để Upload tự POST — đã dispatch qua epic dùng chung axios instance (auth header, error notification).
    return false
  }

  const onRemoveLogo = () => {
    if (matchedApplication) {
      dispatch(OpenIddictApplicationActions.removeLogo(matchedApplication.id))
    }
  }

  const onSubmit = () => {
    form.validateFields().then(values => {
      const redirectUris = (values.redirectUris || []).map((x: UriField) =>
        x.uri.trim(),
      )
      const postLogoutRedirectUris = (values.postLogoutRedirectUris || []).map(
        (x: UriField) => x.uri.trim(),
      )
      const scopes = values.scopes || []
      const allowedOrigins: string[] = (values.allowedOrigins || []).map(
        (x: OriginField) => x.origin.trim(),
      )
      // Để trống = null -> backend xoá override, client dùng global OpenIddict:AccessTokenLifetimeSeconds.
      const accessTokenLifetimeSeconds = values.accessTokenLifetimeSeconds ?? null

      if (isEdit && product) {
        dispatch(
          ProductActions.updateProduct({
            id: product.guid,
            data: { name: values.name.trim(), clientId: product.clientId },
            // One Save = one toast: when the Application is updated too, its own success toast covers both.
            silent: !!matchedApplication,
          }),
        )
        // Không có Application tương ứng (Product tạo trước khi có OpenIddict, hoặc lệch dữ liệu) ->
        // chỉ lưu Name, các field Application-only vốn đã bị ẩn khỏi form (xem JSX bên dưới).
        if (matchedApplication) {
          dispatch(
            OpenIddictApplicationActions.updateApplication({
              id: matchedApplication.id,
              data: {
                displayName: values.displayName.trim(),
                redirectUris,
                postLogoutRedirectUris,
                scopes,
                allowedOrigins,
                allowPasswordGrant,
                accessTokenLifetimeSeconds,
              },
            }),
          )
        }
      } else {
        // Create: chỉ tạo Application — Product được identity-service tự tạo kèm theo.
        dispatch(
          OpenIddictApplicationActions.createApplication({
            clientId: values.clientId.trim(),
            displayName: values.displayName.trim(),
            redirectUris,
            postLogoutRedirectUris,
            scopes,
            allowedOrigins,
            accessTokenLifetimeSeconds,
          }),
        )
      }
    }).catch(() => {
      // Validation failed — antd already shows the field errors; swallow the rejection so it doesn't
      // surface as an unhandled promise rejection (webpack-dev-server overlay blocks the modal in dev).
    })
  }

  // Chỉ check "là absolute URI hợp lệ" — khớp đúng Uri.TryCreate(raw, UriKind.Absolute) phía backend,
  // không ép riêng http/https (không tự sửa/normalize giá trị người dùng nhập).
  const uriRule = {
    validator: async (_: unknown, value: string) => {
      if (!value || !value.trim()) {
        throw new Error(t('fieldRequired'))
      }
      try {
        // eslint-disable-next-line no-new
        new URL(value.trim())
      } catch {
        throw new Error(t('admin.invalidUri'))
      }
    },
  }

  const duplicateUriValidator = async (_: unknown, uris: UriField[]) => {
    const values = (uris || []).map(u => u?.uri?.trim()).filter(Boolean)
    if (new Set(values).size !== values.length) {
      throw new Error(t('admin.duplicateUri'))
    }
  }

  // Origin = scheme://host[:port], không path/query/userinfo, không '*' — khớp OriginNormalizer phía backend.
  const parseOrigin = (raw: string): string | null => {
    try {
      const url = new URL(raw.trim())
      const ok =
        (url.protocol === 'http:' || url.protocol === 'https:') &&
        !url.username &&
        !url.search &&
        !url.hash &&
        url.pathname === '/'
      return ok ? url.origin : null
    } catch {
      return null
    }
  }

  const originRule = {
    validator: async (_: unknown, value: string) => {
      if (!value || !value.trim()) {
        throw new Error(t('fieldRequired'))
      }
      if (!parseOrigin(value)) {
        throw new Error(t('admin.invalidOrigin', { origin: value }))
      }
    },
  }

  const duplicateOriginValidator = async (_: unknown, origins: OriginField[]) => {
    const seen = new Set<string>()
    for (const item of origins || []) {
      const origin = item?.origin ? parseOrigin(item.origin) : null
      if (!origin) continue
      if (seen.has(origin)) throw new Error(t('admin.duplicateOrigin', { origin }))
      seen.add(origin)
    }
  }

  const renderClientType =(clientType: string | null) => {
    if (clientType === 'confidential') {
      return <Tag color="orange">{t('admin.clientTypeConfidential')}</Tag>
    }
    if (clientType === 'public') {
      return <Tag color="blue">{t('admin.clientTypePublic')}</Tag>
    }
    return <Tag>{t('admin.unknown')}</Tag>
  }

  // Ở Edit, không tìm được Application tương ứng (ClientId lệch/Product tạo tay ngoài luồng) -> chỉ
  // cho sửa Name, ẩn hẳn các field Application-only thay vì hiển thị form trống gây hiểu lầm.
  const showApplicationFields = !isEdit || !!matchedApplication

  return (
    <Modal
      visible={visible}
      title={isEdit ? t('admin.editProduct') : t('admin.newProduct')}
      confirmLoading={isSavingProduct || isSavingApplication}
      onCancel={close}
      onOk={onSubmit}
      okText={t('save')}
      cancelText={t('cancel')}
      width={640}
      bodyStyle={{ maxHeight: 'calc(100vh - 260px)', overflowY: 'auto' }}
    >
      <Spin spinning={isEdit && isLoadingApplications}>
        <Form form={form} layout="vertical" autoComplete="off">
          <Tabs defaultActiveKey="1">
            <Tabs.TabPane tab={t('admin.generalInfoTab')} key="1">
              <Form.Item
                label={t('admin.name')}
                name="name"
                rules={[
                  { required: true, whitespace: true, message: t('fieldRequired') },
                ]}
              >
                <Input placeholder="Ví dụ: HICAS System Admin" />
              </Form.Item>

              <Form.Item
                label={t('admin.clientId')}
                name="clientId"
                extra={isEdit ? t('admin.clientIdNotEditable') : undefined}
                rules={[
                  { required: true, whitespace: true, message: t('fieldRequired') },
                ]}
              >
                <Input disabled={isEdit} />
              </Form.Item>

              {isEdit && matchedApplication && (
                <Form.Item label={t('admin.clientType')}>
                  <Space>
                    {renderClientType(matchedApplication.clientType)}
                    <Tag color={matchedApplication.requirePkce ? 'green' : 'red'}>
                      PKCE: {matchedApplication.requirePkce ? t('admin.yes') : t('admin.no')}
                    </Tag>
                  </Space>
                </Form.Item>
              )}

              {isEdit && matchedApplication && (
                <Form.Item
                  label={t('admin.allowPasswordGrant')}
                  extra={t('admin.allowPasswordGrantHint')}
                >
                  <Switch
                    checked={allowPasswordGrant}
                    onChange={onTogglePasswordGrant}
                    data-testid="product-allow-password-grant-switch"
                  />
                </Form.Item>
              )}

              {showApplicationFields && (
                <Form.Item
                  label={t('admin.accessTokenLifetime')}
                  name="accessTokenLifetimeSeconds"
                  extra={t('admin.accessTokenLifetimeHint')}
                  rules={[
                    {
                      type: 'number',
                      min: MIN_ACCESS_TOKEN_LIFETIME_SECONDS,
                      max: MAX_ACCESS_TOKEN_LIFETIME_SECONDS,
                      message: t('admin.accessTokenLifetimeRange', {
                        min: MIN_ACCESS_TOKEN_LIFETIME_SECONDS,
                        max: MAX_ACCESS_TOKEN_LIFETIME_SECONDS,
                      }),
                    },
                  ]}
                >
                  <InputNumber
                    precision={0}
                    placeholder={t('admin.accessTokenLifetimePlaceholder')}
                    addonAfter={t('admin.seconds')}
                    style={{ width: '100%' }}
                    data-testid="product-access-token-lifetime-input"
                  />
                </Form.Item>
              )}

              {isEdit && !matchedApplication && (
                <Form.Item>
                  <span style={{ color: '#999' }}>{t('admin.noLinkedApplication')}</span>
                </Form.Item>
              )}

              {showApplicationFields && (
                <>
                  <Form.Item
                    label={t('admin.displayName')}
                    name="displayName"
                    rules={[
                      { required: true, whitespace: true, message: t('fieldRequired') },
                    ]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item label={t('admin.redirectUri')} required>
                    <Form.List
                      name="redirectUris"
                      rules={[
                        {
                          validator: async (_, uris) => {
                            if (!uris || uris.length === 0) {
                              throw new Error(t('admin.atLeastOneUri'))
                            }
                            await duplicateUriValidator(_, uris)
                          },
                        },
                      ]}
                    >
                      {(fields, { add, remove }, { errors }) => (
                        <>
                          {fields.map(field => (
                            <Form.Item
                              required={false}
                              key={field.key}
                              style={{ marginBottom: 8 }}
                            >
                              <Space style={{ display: 'flex' }} align="baseline">
                                <Form.Item
                                  {...field}
                                  name={[field.name, 'uri']}
                                  noStyle
                                  rules={[uriRule]}
                                >
                                  <Input
                                    style={{ width: 460 }}
                                    placeholder="https://app.example.com/callback"
                                  />
                                </Form.Item>
                                <MinusCircleOutlined onClick={() => remove(field.name)} />
                              </Space>
                            </Form.Item>
                          ))}
                          <Form.Item>
                            <Button
                              type="dashed"
                              onClick={() => add()}
                              icon={<PlusOutlined />}
                            >
                              {t('admin.addUri')}
                            </Button>
                            <Form.ErrorList errors={errors} />
                          </Form.Item>
                        </>
                      )}
                    </Form.List>
                  </Form.Item>

                  <Form.Item label={t('admin.postLogoutRedirectUri')}>
                    <Form.List
                      name="postLogoutRedirectUris"
                      rules={[{ validator: duplicateUriValidator }]}
                    >
                      {(fields, { add, remove }, { errors }) => (
                        <>
                          {fields.map(field => (
                            <Form.Item
                              required={false}
                              key={field.key}
                              style={{ marginBottom: 8 }}
                            >
                              <Space style={{ display: 'flex' }} align="baseline">
                                <Form.Item
                                  {...field}
                                  name={[field.name, 'uri']}
                                  noStyle
                                  rules={[uriRule]}
                                >
                                  <Input
                                    style={{ width: 460 }}
                                    placeholder="https://app.example.com/login"
                                  />
                                </Form.Item>
                                <MinusCircleOutlined onClick={() => remove(field.name)} />
                              </Space>
                            </Form.Item>
                          ))}
                          <Form.Item>
                            <Button
                              type="dashed"
                              onClick={() => add()}
                              icon={<PlusOutlined />}
                            >
                              {t('admin.addUri')}
                            </Button>
                            <Form.ErrorList errors={errors} />
                          </Form.Item>
                        </>
                      )}
                    </Form.List>
                  </Form.Item>

                  <Form.Item label={t('admin.scopes')} name="scopes">
                    <Select mode="multiple" placeholder={t('admin.selectScopes')}>
                      {AVAILABLE_SCOPES.map(s => (
                        <Option key={s} value={s}>
                          {s}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label={t('admin.allowedOrigins')}
                    extra={t('admin.allowedOriginsHint')}
                  >
                    <Form.List
                      name="allowedOrigins"
                      rules={[{ validator: duplicateOriginValidator }]}
                    >
                      {(fields, { add, remove }, { errors }) => (
                        <>
                          {fields.map(field => (
                            <Form.Item
                              required={false}
                              key={field.key}
                              style={{ marginBottom: 8 }}
                            >
                              <Space style={{ display: 'flex' }} align="baseline">
                                <Form.Item
                                  {...field}
                                  name={[field.name, 'origin']}
                                  noStyle
                                  rules={[originRule]}
                                >
                                  <Input
                                    style={{ width: 460 }}
                                    placeholder={t('admin.allowedOriginsPlaceholder')}
                                    data-testid={`product-allowed-origin-input-${field.name}`}
                                  />
                                </Form.Item>
                                <MinusCircleOutlined onClick={() => remove(field.name)} />
                              </Space>
                            </Form.Item>
                          ))}
                          <Form.Item>
                            <Button
                              type="dashed"
                              onClick={() => add()}
                              icon={<PlusOutlined />}
                              data-testid="product-add-allowed-origin"
                            >
                              {t('admin.addOrigin')}
                            </Button>
                            <Form.ErrorList errors={errors} />
                          </Form.Item>
                        </>
                      )}
                    </Form.List>
                  </Form.Item>
                </>
              )}
            </Tabs.TabPane>

            {showApplicationFields && (
              <Tabs.TabPane tab={t('admin.loginBrandingTab')} key="2">
                <Form.Item label={t('admin.logo')}>
                  {isEdit && matchedApplication ? (
                    <Space align="center">
                      {matchedApplication.logoUrl ? (
                        <img
                          src={resolveIdentityAssetUrl(matchedApplication.logoUrl)}
                          alt={matchedApplication.displayName}
                          style={{
                            width: 96, height: 48, objectFit: 'contain', border: '1px solid #f0f0f0', borderRadius: 4,
                          }}
                        />
                      ) : (
                        <span style={{ color: '#999' }}>{t('admin.noLogo')}</span>
                      )}
                      <Upload accept=".png,.jpg,.jpeg,.webp" showUploadList={false} beforeUpload={beforeUploadLogo}>
                        <Button icon={<UploadOutlined />} loading={isLogoSaving}>
                          {matchedApplication.logoUrl ? t('admin.changeLogo') : t('admin.uploadLogo')}
                        </Button>
                      </Upload>
                      {matchedApplication.logoUrl && (
                        <Popconfirm title={t('admin.confirmRemoveLogo')} onConfirm={onRemoveLogo}>
                          <Button danger icon={<DeleteOutlined />} loading={isLogoSaving}>
                            {t('admin.removeLogo')}
                          </Button>
                        </Popconfirm>
                      )}
                    </Space>
                  ) : (
                    <span style={{ color: '#999' }}>{t('admin.logoAvailableAfterCreate')}</span>
                  )}
                </Form.Item>

                {isEdit && matchedApplication && (
                  <Form.Item>
                    <LoginBrandingSection applicationId={matchedApplication.id} />
                  </Form.Item>
                )}
              </Tabs.TabPane>
            )}
          </Tabs>
        </Form>
      </Spin>
    </Modal>
  )
}
